import React, { ChangeEvent, FunctionComponent, useEffect, useRef, useState } from 'react';
import { IOption, ISchemaFieldProps } from '../../model/IJsonSchema';
import Autocomplete, { createFilterOptions } from '@material-ui/lab/Autocomplete';
import { InputAdornment } from '@material-ui/core';
import GenericInput from '../../../../common/components/genericInput/GenericInput';
import genericInputStyles from '../../../../common/components/genericInput/GenericInputStyles';
import { KeyboardArrowDown } from '@material-ui/icons';
import usePlatformConfig from '../../../../../common/react/usePlatformConfig';
import useCheckoutContext from '../../../../../gql/common/hooks/useCheckoutContext';
import useJourneyStepsContext from '../../../../../gql/checkout4/hooks/useJourneyStepsContext';
import useNewDxpDigitalAssignmentEntryEvent from '../../../../../gql/common/hooks/useNewDxpDigitalAssignmentEntryEvents';
import { EventDataTypes } from '../../../../../gql/common/enum/DataLayerEnums';
import {
  getCheckoutStepsCommonFields,
  getEventDataCommonFields,
  getTradeInCommonFields,
  vproductCommonFields,
  removeHostNameFromUrl,
} from '../../../../../gql/common/utils/dxpDataLayerCommonFields';
import { AutocompleteInputId } from '../../../../../common/enum/TradeInComponentId';
import { DxpDataLayerEventNames } from '../../../../../../../common/utilities/customDatalayerInterface';

const AutocompleteInput: FunctionComponent<ISchemaFieldProps> = ({ formData }) => {
  const { label, required, id, value, options, placeholder, schema } = formData;
  const styles = genericInputStyles();
  const [dropdownOpened, setDropdownOpen] = useState(false);
  const defaultValue = options.enumOptions.find(option => option.value === value);
  const filterOptions = createFilterOptions<IOption>({
    matchFrom: 'any',
    limit: 50,
  });

  const {
    platformConfig: { checkout, ga4DxpDataLayer },
  } = usePlatformConfig();

  const { deal } = useCheckoutContext();
  const { steps, activeStep } = useJourneyStepsContext();
  const { jlrDataLayer } = ga4DxpDataLayer ?? {};
  const { callDxpDigitalAssignmentEntryEvent } = useNewDxpDigitalAssignmentEntryEvent();

  const { showDropdownIcon } = checkout || {};

  const handleOnChange = (option: IOption) => {
    setDropdownOpen(false);

    const { onChange, id, formContext } = formData;
    const { tradeIn } = formContext;
    onChange(option?.value);

    if (deal && id === AutocompleteInputId.TRADEIN_BRAND) {
      const data = {
        event_action: removeHostNameFromUrl(window.location.href),
        event_category: EventDataTypes.CHECKOUT_CLICK,
        event_label: `${EventDataTypes.DROPDOWN}::${EventDataTypes.TRADEIN_BRAND}`,
        event_pagecategory: EventDataTypes.CHECKOUT,
        click_url: removeHostNameFromUrl(window.location.href),
        click_text: EventDataTypes.TRADEIN_BRAND,
        click_name: EventDataTypes.DROPDOWN,
        vproduct: {
          ...vproductCommonFields(deal),
          total_weight: '',
          max_speed: '',
          est_delivery_time: deal.car?.deliveryTimeFrame?.toString() ?? '',
        },
        checkoutStep: {
          ...getCheckoutStepsCommonFields(steps, activeStep),
        },
        tradein: {
          ...getTradeInCommonFields(deal, tradeIn),
          brand: option?.value.toLowerCase(),
        },
      };

      callDxpDigitalAssignmentEntryEvent(
        {
          ...data,
          ...getEventDataCommonFields(DxpDataLayerEventNames.TRADEIN_BRAND_CTA_CLICK, true),
        },
        jlrDataLayer,
      );

      const { model, mileage, year, registrationNumber } = tradeIn || {};
      if (value && !!model && !!mileage && !!year && !!registrationNumber)
        callDxpDigitalAssignmentEntryEvent(
          {
            ...getEventDataCommonFields(DxpDataLayerEventNames.TRADEIN_DETAILS_CTA_CLICK, true),
            ...data,
          },
          jlrDataLayer,
        );
    }
  };

  const handleBodyScroll = () => {
    if (dropdownOpened) {
      setDropdownOpen(false);
    }
  };

  const autocompleteRef = useRef<HTMLDivElement | null>(null);

  const handleCloseDropdownOnPageClick = (event: MouseEvent) => {
    if (autocompleteRef.current && !autocompleteRef.current.contains(event.target as Node)) {
      setDropdownOpen(false);
    }
  };

  useEffect(() => {
    const body = document.body;
    const scrollbarWidth = window.innerWidth - body.clientWidth;

    body.style.overflow = dropdownOpened ? 'hidden' : 'auto';
    body.style.paddingRight = dropdownOpened ? `${scrollbarWidth}px` : 'unset';
    body.addEventListener('scroll', handleBodyScroll);
    document.addEventListener('click', handleCloseDropdownOnPageClick);

    return () => {
      body.style.overflow = 'auto';
      body.style.paddingRight = 'unset';
      body.removeEventListener('scroll', handleBodyScroll);
      document.removeEventListener('click', handleCloseDropdownOnPageClick);
    };
  }, [dropdownOpened]);

  return (
    <div ref={autocompleteRef}>
      <Autocomplete
        open={dropdownOpened}
        filterOptions={filterOptions}
        noOptionsText={options.invalidOption}
        onChange={(_: ChangeEvent<{ value?: string }>, option: IOption) => handleOnChange(option)}
        onOpen={() => !schema?.readOnly && setDropdownOpen(true)}
        onClose={() => setDropdownOpen(false)}
        classes={{
          input: styles.input,
          noOptions: styles.noOptions,
          listbox: styles.listbox,
          option: styles.option,
          paper: styles.paper,
        }}
        id={id}
        options={options.enumOptions}
        getOptionLabel={option => option.label}
        disableClearable={schema?.readOnly}
        defaultValue={defaultValue}
        data-test={`autocomplete_input:${id}`}
        renderInput={params => {
          const inputProps = {
            label,
            required,
            id,
            value,
            params,
            placeholder,
            tooltip: options.tooltip,
            readOnly: schema?.readOnly,
            endAdornment: (
              <InputAdornment position="end">
                {showDropdownIcon && (
                  <KeyboardArrowDown onClick={() => setDropdownOpen(true)} className={styles.arrowDownIconButton} />
                )}
              </InputAdornment>
            ),
          };
          if (options.displayKeyOnSelect && !dropdownOpened) {
            params.inputProps = { ...params.inputProps, value };
            return <GenericInput {...inputProps} />;
          }

          return <GenericInput {...inputProps} />;
        }}
      />
    </div>
  );
};

export default AutocompleteInput;
